import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Thumbs } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useMediaQuery } from 'usehooks-ts'


import { formatPriceAmount, isAgeRestrictedProduct } from '@entities/Product/helpers'

import ControlCart from '@features/cart/ui/control'
import { FavoriteButton } from '@features/favorite'
import { useGalleryModal, useGalleryStore } from '@features/gallery'
import { useCategoriesModal } from '@widgets/modal-categories/hooks/useCategoriesModal'

import { URL_MAIN_PAGE } from '@shared/contants'
import { MarketingLabel, Product } from '@shared/generated'
import { useAgeVerification } from '@shared/hooks'
import { cn } from '@shared/lib/cn'
import BadgeGroup from '@shared/ui/badge/group'
import Badge from '@shared/ui/badge/item'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Button from '@shared/ui/button'
import ImageWithBg from '@shared/ui/image-with-bg'
import RatingPreview from '@shared/ui/rating/preview/preview'
import { formatPrice } from '@shared/uttils'
import { getProductGalleryImages } from '@shared/uttils/getGalleryImages'
import getNoun from '@shared/uttils/getNoun'

import ProductTabs from './ProductTabs'

import styles from './styles.module.scss'

import type { Swiper as SwiperType } from 'swiper'

interface ProductDetailProps {
  product: Product;
}


export default function ProductDetail ({ product }: ProductDetailProps) {
  // const [ showStickyCard, setShowStickyCard ] = useState<boolean>(false)
  const [ thumbsSwiper, setThumbsSwiper ] = useState<SwiperType | null>(null)
  const { openModal: openCategoriesModal } = useCategoriesModal()
  const navigate = useNavigate()
  const hasOpenedAgeGateRef = useRef(false)
  const { isAdult, guardAgeRestrictedAction } = useAgeVerification()

  const { t: tn } = useTranslation('navigation')
  const { t: tp } = useTranslation('product')

  const isTablet = useMediaQuery('(max-width: 991px)')

  const { openGalleryModal } = useGalleryModal()
  const { setImages } = useGalleryStore()

  // const { ref: observerRef, isIntersecting } = useIntersectionObserver()

  const galleryImages = getProductGalleryImages(product, false)
  const fullGalleryImages = getProductGalleryImages(product, true)

  const handleClickImage = () => {
    setImages(fullGalleryImages)
    openGalleryModal()
  }

  const isAgeControlRequired = !isAdult && isAgeRestrictedProduct(product)

  useEffect(() => {
    if (!isAgeControlRequired || hasOpenedAgeGateRef.current) {
      return
    }

    hasOpenedAgeGateRef.current = true

    guardAgeRestrictedAction(true, () => undefined, {
      onCancel: () => {
        if (window.history.length > 1) {
          navigate(-1)
          return
        }

        navigate(URL_MAIN_PAGE)
      },
    })
  }, [ guardAgeRestrictedAction, isAgeControlRequired, navigate ])

  // TODO: показываем закрепленную карточку
  // useEffect(() => {
  //   setShowStickyCard(!isIntersecting)
  // }, [ isIntersecting ])

  return (
    <div className={styles.root}>
      {/* <div className={styles.root__sticky}>
        {showStickyCard && <StickyCard product={product} />}
      </div> */}

      <Breadcrumbs
        className={styles.root__breadcrumbs}
        links={[
          {
            title: tn('catalog'),
            // to: URL_PAGE_CATALOG,
            onClick: () => {
              openCategoriesModal()
            },
          },
          {
            title: product.name,
          },
        ]}
      />

      <div className={styles.root__grid}>
        <div className={styles.root__photos}>
          <div className={styles['root__photos-main']}>
            <Swiper
              slidesPerView={isTablet ? 1.1 : 1}
              spaceBetween={isTablet ? 16 : 0}
              modules={[ Thumbs ]}
              thumbs={{ swiper: thumbsSwiper }}
            >
              {galleryImages.map((url, index) => (
                <SwiperSlide key={index}>
                  <ImageWithBg
                    className={styles['root__main-photo']}
                    src={url}
                    onClick={handleClickImage}
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            <BadgeGroup className={styles.root__badges}>
              {product.marketingLabels?.map((item) => (
                <Badge data={item as MarketingLabel} key={item?.id} />
              ))}
              {isAgeControlRequired && (
                <Badge
                  data={
                    {
                      title: '18+', // TODO: Move 18 to constants
                      optionBackgroundColor: '#434242',
                    } as MarketingLabel
                  }
                />
              )}
            </BadgeGroup>
          </div>

          {!isTablet && (
            <div className={styles.root__thumbs}>
              <div className={styles['root__thumbs-slider']}>
                <Swiper
                  onSwiper={setThumbsSwiper}
                  spaceBetween={10}
                  slidesPerView="auto"
                  watchSlidesProgress
                  className={styles['root__thumbs-swiper']}
                  direction="vertical"
                >
                  {galleryImages.map((url, index) => (
                    <SwiperSlide
                      key={index}
                      className={styles['root__thumbs-slide']}
                    >
                      <ImageWithBg
                        className={styles['root__thumbs-img']}
                        src={url}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          )}
        </div>

        <div className={styles.root_info}>
          <div className={styles.root__item}>
            <h1 className={styles.root__title}>{product.name}</h1>
            <div className={styles.root__chain}>
              {product.xid && (
                <div className={styles['root__chain-item']}>
                  <span className={styles.root__article}>
                    {tp('product.article')}: {product.xid}
                  </span>
                </div>
              )}
              <div className={styles['root__chain-item']}>
                <div className={styles.root__rating}>
                  <RatingPreview
                    className={styles['root__rating-star']}
                    classNameText={styles['root__rating-value']}
                    value={product.average_rating as number}
                  />
                  <span
                    className={cn(styles['root__rating-count'], 'rating-count-global')}
                  >
                    {product.review_count}{' '}
                    {getNoun(
                      product.review_count as number,
                      tp('productReview.1'),
                      tp('productReview.2'),
                      tp('productReview.3'),
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.root__item}>
            <div className={styles['root__price-wrapper']}>
              {product.priceOffer?.price != null && (
                <div
                  className={cn(styles.root__price, {
                    [styles._accent]: !!product.oldPrice,
                  })}
                >
                  {formatPrice(product.priceOffer.price)}{' '}
                  <span>/ {formatPriceAmount({ priceOffer: product.priceOffer, t: tp })}</span>
                </div>
              )}
              {!!product.priceOffer?.oldPrice && (
                <div className={styles['root__old-price']}>
                  {formatPrice(product.priceOffer.oldPrice)}
                </div>
              )}
            </div>
          </div>

          <div
            className={cn(styles.root__item, styles.root__group)}
            // ref={observerRef}
          >
            {!!product.stock?.amount && (
              <ControlCart size="large" isTablet={false} product={product} />
            )}
            {!product.stock?.amount && (
              <Button size="large" disabled>
                {tp('product.out_of_stock')}
              </Button>
            )}
            <FavoriteButton
              className={styles.root__favorite}
              id={product.id}
              isFavorite={!!product.isFavorite}
              size='large'
              appearance='button'
            />
          </div>

          {product.ingredients && (
            <div className={cn(styles.root__item, styles.root__ingredients)}>
              <h3>{tp('product.ingredients')}:</h3>
              <p>{product.ingredients}</p>
            </div>
          )}
        </div>
      </div>

      <ProductTabs product={product} />
    </div>
  )
}
